import React, { useState } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";

const AddCourse = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  axios.defaults.withCredentials = true;

  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const onSubmit = async (data) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/course/add-course`,
        data
      );
      if (response.status === 200) {
        setMessage("Course added successfully.");
        document.getElementById("courseName").value = "";
        document.getElementById("section").value = "";
        document.getElementById("year").value = "";
      }
    } catch (err) {
      console.error(err);
      setMessage("Failed to add course. Try again.");
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (["dragenter", "dragover"].includes(e.type)) {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setMessage("Please upload a CSV file.");
      return;
    }

    setLoading(true);
    const reader = new FileReader();
    reader.onload = async (event) => {
      const csvText = event.target.result;
      const lines = csvText.trim().split("\n");
      const headers = lines[0].split(",");

      if (!headers.includes("courseName") || !headers.includes("section") || !headers.includes("year")) {
        setMessage("CSV must contain 'courseName', 'section', and 'year' columns.");
        setLoading(false);
        return;
      }

      const courses = lines.slice(1).map((line) => {
        const values = line.split(",");
        const course = {};
        headers.forEach((header, index) => {
          course[header.trim()] = values[index]?.trim() ?? "";
        });
        return course;
      });

      try {
        await Promise.all(
          courses.map((course) =>
            axios.post(
              `${process.env.REACT_APP_BASE_URL}/course/add-course`,
              course,
              { withCredentials: true }
            )
          )
        );
        setMessage("All courses uploaded successfully.");
      } catch (error) {
        setMessage("Some courses may not have been uploaded. Check the server.");
      } finally {
        setLoading(false);
        setFile(null);
      }
    };

    reader.readAsText(file);
  };

  return (
    <div className="max-w-3xl mx-auto mt-10 p-6 bg-white shadow-md rounded-lg">
      <h1 className="text-xl font-bold text-center mb-6">Add Course</h1>

      {/* Add Course Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mb-6">
        {/* Course Name */}
        <div>
          <label
            htmlFor="courseName"
            className="block text-gray-700 font-medium mb-1"
          >
            Course Name
          </label>
          <input
            id="courseName"
            type="text"
            {...register("courseName", {
              required: "Course name is required",
            })}
            placeholder="Enter course name"
            className={`w-full p-3 border ${
              errors.courseName ? "border-red-500" : "border-gray-300"
            } rounded-lg focus:outline-none focus:ring focus:ring-blue-300`}
          />
          {errors.courseName && (
            <p className="text-red-500 text-sm mt-1">
              {errors.courseName.message}
            </p>
          )}
        </div>

        {/* Section */}
        <div>
          <label
            htmlFor="section"
            className="block text-gray-700 font-medium mb-1"
          >
            Section
          </label>
          <input
            id="section"
            type="text"
            {...register("section", {
              required: "Section is required",
            })}
            placeholder="Enter section"
            className={`w-full p-3 border ${
              errors.section ? "border-red-500" : "border-gray-300"
            } rounded-lg focus:outline-none focus:ring focus:ring-blue-300`}
          />
          {errors.section && (
            <p className="text-red-500 text-sm mt-1">
              {errors.section.message}
            </p>
          )}
        </div>

        {/* Year */}
        <div>
          <label
            htmlFor="year"
            className="block text-gray-700 font-medium mb-1"
          >
            Year
          </label>
          <input
            id="year"
            type="number"
            {...register("year", {
              required: "Year is required",
              min: {
                value: 1900,
                message: "Year must be at least 1900",
              },
              max: {
                value: 2100,
                message: "Year cannot be more than 2100",
              },
            })}
            placeholder="Enter year"
            className={`w-full p-3 border ${
              errors.year ? "border-red-500" : "border-gray-300"
            } rounded-lg focus:outline-none focus:ring focus:ring-blue-300`}
          />
          {errors.year && (
            <p className="text-red-500 text-sm mt-1">{errors.year.message}</p>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition duration-300"
        >
          Submit
        </button>
      </form>

      {/* CSV File Upload */}
      <form
        onSubmit={handleFileSubmit}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        className="space-y-4"
      >
        <div className="mb-4">
          <label className="block mb-2 text-gray-700 font-semibold">
            Upload Courses via CSV:
          </label>

          <label
            htmlFor="csvUpload"
            className={`flex items-center justify-center w-full px-4 py-6 border-2 border-dashed rounded-md cursor-pointer transition ${
              dragActive
                ? "border-blue-400 bg-blue-50"
                : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-blue-500 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0l-4 4m4-4l4 4M17 8v12m0 0l-4-4m4 4l4-4" />
            </svg>
            {file ? (
              <span className="text-sm text-gray-700">{file.name}</span>
            ) : (
              <span className="text-sm text-gray-500">
                Click to select or drag a CSV file here
              </span>
            )}
            <input
              id="csvUpload"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
            />
          </label>
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`w-full py-2 mt-2 font-semibold rounded-md transition duration-200 ${
            loading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-blue-500 hover:bg-blue-600 text-white"
          }`}
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin h-5 w-5 mr-2 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 0115.73-3.29"
                ></path>
              </svg>
              Uploading...
            </span>
          ) : (
            "Upload CSV"
          )}
        </button>
      </form>

      {message && <p className="mt-4 text-center">{message}</p>}
    </div>
  );
};

export default AddCourse;
